import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import Title2Component from '../../components/title/title2Component';
import { useTranslation } from 'react-i18next';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CallIcon from '@mui/icons-material/Call';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import data from '../../data/data.json';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';

const ContactView = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    const contactDetailsList = [
        {
            color: "#828815",
            icon: <HandshakeIcon sx={{color: 'white'}}/>,
            title: "contact.investment.title",
            description: "contact.investment.description",
            link: {
                label: data.contact.mobile.label,
                to: data.contact.mobile.whatsappLink
            }
        },
        {
            color: "#158822",
            icon: <CallIcon sx={{color: 'white'}}/>,
            title: "contact.help.title",
            description: "contact.help.description",
            link: {
                label: data.contact.mobile.label,
                to: data.contact.mobile.whatsappLink
            }
        },
        {
            color: "#156688",
            icon: <EmailIcon sx={{color: 'white'}}/>,
            title: "contact.location.title",
            description: "contact.location.description",
            link: {
                label: data.contact.email,
                to: `mailto:${data.contact.email}`
            }
        },
        {
            color: "#801588",
            icon: <CalendarMonthIcon sx={{color: 'white'}}/>,
            title: "contact.call.title",
            description: "contact.call.description",
            link: {
                label: t('contact.calender'),
                to: data.contact.calender
            }
        }
    ]

    const [height, setHeight] = useState(600);

    useEffect(() => {
        const handleResize = () => {
            const contactBoxes = document.getElementById('contactBoxes');
            const contactFormHeight = contactBoxes ? contactBoxes.offsetHeight : 0;
    
            setHeight(contactFormHeight);
        };
    
        handleResize();
    
        window.addEventListener('resize', handleResize);
    
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='center'
                pt={{xs: 15, md: 20}}
                pb={{xs: 2, md: 5}}
            >
                <Title2Component 
                    title={t('contact.title')} 
                    description={t('contact.description')}
                    needWhite
                />

                <Grid
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    container
                    spacing={{xs: 1, md: 5}}
                    className="bodyPadding"
                >
                    <Grid size={{xs: 12, md: 6}}>
                        <iframe 
                            aria-label='Let’s Talk with Cuddles!' 
                            frameborder="0" 
                            style={{
                                height: `${height}px`,
                                width:"100%",
                                border:"none",
                                backgroundColor: 'white',
                                borderRadius: '16px'
                            }}
                            src='https://forms.zohopublic.com/edlienamobilegm1/form/contactus/formperma/GE7I53US_gZBoVyU7vcEwPKbYlHtAlO1u_APMom0n5I'
                        >
                        </iframe>
                    </Grid>

                    <Grid 
                        size={{xs: 12, md: 6}}
                        container
                        id="contactBoxes"
                    >
                        {
                            contactDetailsList.map((item, index) => {
                                return <Grid 
                                    key={index}
                                    size={{xs: 12, md: 6}}
                                    sx={{
                                        width: '100%'
                                    }}
                                    data-aos="zoom-in-down"
                                    data-aos-delay={index * 150}
                                    data-aos-duration="1000"
                                >
                                    <ContactDetails data={item}/>
                                </Grid>
                            })
                        }
                    </Grid>
                </Grid>

                <Stack
                    position='relative'
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    sx={{
                        backgroundColor: 'white',
                        mt: {xs: 5, md: 10}
                    }}
                >
                    {/* Upper cloud */}
                    <Box
                        sx={{
                            width: '100%',
                            zIndex: 1,
                            backgroundColor: theme.palette.primary.main,
                            transform: 'scaleY(-1) translateY(2px)',
                        }}
                    >
                        <img 
                            src='/images/vectors/wave bg.png' 
                            alt='cloud' 
                            width='100%' 
                            data-aos="fade-down"
                            data-aos-duration="500"
                            data-aos-once="true"
                        />
                    </Box>

                    {/* Body */}
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        px={{xs: 3, md: 15, lg: 25}}
                        sx={{
                            zIndex: 2,
                            transform: 'translateY(1px)',
                        }}
                        spacing={1}
                    >
                        <Title2Component 
                            title={t('contact.faq.title')} 
                            description={t('contact.faq.description')}
                        />

                        {
                            [1,2,3,4,5].map((item, index) => {
                                return <FaqDetails 
                                    key={index}
                                    question={t(`contact.faq.faq${item}.question`)}
                                    answer={t(`contact.faq.faq${item}.answer`)}
                                    removeMargin={item === 1}
                                    expanded={item === 1}
                                />
                            })
                        }
                    </Stack>

                    {/* Lower cloud */}
                    <Box
                        sx={{
                            width: '100%',
                            zIndex: 1,
                            backgroundColor: theme.palette.primary.main,
                            transform: 'translateY(2px)'
                        }}
                    >
                        <img 
                            src='/images/vectors/wave bg.png' 
                            alt='cloud' 
                            width='100%' 
                            data-aos="fade-down"
                            data-aos-duration="500"
                            data-aos-once="true"
                        />
                    </Box>
                </Stack>
            </Stack>
        </>
    );
}

export default ContactView;


const ContactDetails = ({data}) => {
    const {t} = useTranslation();

    return (
        <>
            <Box
                sx={{
                    borderRadius: 4,
                    backgroundColor: data.color,
                    p: {xs: 2, md: 5},
                    minHeight: {xs: 100, md: 250, lg: 200}
                }}
            >
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='start'
                    spacing={{xs: 1, md: 2}}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        {data.icon}
                    </Box>

                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: {xs: 16, md: 20, lg: 32},
                            color: 'white',
                            lineHeight: 1
                        }}
                    >
                        {t(data.title)}
                    </Typography>

                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: {xs: 12, md: 14, lg: 16},
                            textAlign: 'left',
                            color: 'white'
                        }}
                    >
                        {t(data.description)}
                    </Typography>

                    <Link
                        to={data.link.to}
                        target='_blank'
                        style={{
                            textDecoration: 'none'
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 400,
                                fontSize: {xs: 12, md: 14, lg: 16},
                                textAlign: 'left',
                                color: 'white',
                                '&: hover': {
                                    transform: 'scale(0.9)'
                                },
                                pt: 2
                            }}
                        >
                            {t(data.link.label)}
                        </Typography>
                    </Link>
                </Stack>
            </Box>
        </>
    );
}

const FaqDetails = ({
    question,
    answer,
    removeMargin=false,
    expanded=false
}) => {
    const theme = useTheme();

    return (
        <>
            <Accordion
                elevation={0}
                sx={{
                    width: '100%',
                    mt: {xs: removeMargin ? 0 : 1, md: removeMargin ? 0 : 2},
                    py: {xs: 1, md: 2},
                    backgroundColor: 'transparent',
                    borderRadius: 4,
                    '&:before': {
                        display: 'none',
                    }
                }}
                defaultExpanded={expanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    <Typography
                        fontWeight={600}
                        fontSize={{xs: 12, md: 16, lg: 20}}
                        textAlign='left'
                        color={theme.palette.text.primary}
                        lineHeight={1.5}
                    >
                        {question}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Typography
                        fontWeight={500}
                        fontSize={{xs: 12, md: 12, lg: 16}}
                        textAlign='left'
                        lineHeight={{xs: 1.5, md: 2}}
                        color={theme.palette.text.secondary}
                    >
                        {answer}
                    </Typography>
                </AccordionDetails>
            </Accordion>
        </>
    );
}